type Attr = { [index: string]: string };

export interface DOMElement {
    tag: string;
    attr?: Attr;
    children?: DOMElement[];
    txt?: string;
}

export function elOf(tag: string, attr: Attr | string | null, children: DOMElement[] | string): DOMElement {
    const res: DOMElement = { tag: tag };

    if (attr != null) {
        if (typeof attr == 'string') {
            attr = { class: attr };
        }
        res.attr = attr;
    }

    if (typeof children == 'string') {
        res.txt = children;
    } else {
        res.children = children;
    }

    return res;
}

export function buildDom(parent: HTMLElement, elements: DOMElement[]): void {
    elements.forEach((el: DOMElement): void => {
        const tag = document.createElement(el.tag);

        if (el.attr) {
            for (const [name, val] of Object.entries(el.attr)) {
                if (name === 'class' || name === 'className') {
                    tag.className = val;
                } else {
                    tag.setAttribute(name, val);
                }
            }
        }

        if (el.children && el.children.length) {
            buildDom(tag, el.children);
        } else if (el.txt) {
            tag.appendChild(document.createTextNode(el.txt));
        }

        parent.appendChild(tag);
    });
}

/**
 * Replaces named placeholders in a language string with DOMElement nodes.
 * Static text parts become safe <span> text nodes — user input in replacements
 * is also safe because elOf/buildDom use createTextNode internally.
 *
 * @param template  - The language string with placeholders, e.g. "No results for #SEARCHTERM#."
 * @param replacements - A map of placeholder strings to their DOMElement replacements.
 * @param wrapperTag - The HTML tag to wrap the result in (default: 'p').
 * @param wrapperClass - Optional CSS class for the wrapper element.
 */
export function buildFromTemplate(
    template: string,
    replacements: Record<string, DOMElement>,
    wrapperTag: string = 'p',
    wrapperClass: string | null = null
): DOMElement {
    const placeholderKeys = Object.keys(replacements);
    // Build a regex that matches any of the placeholder keys (escaped for safety)
    const pattern = new RegExp(
        '(' + placeholderKeys.map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')'
    );
    const parts = template.split(pattern);
    const children: DOMElement[] = [];

    for (const part of parts) {
        if (part.length === 0) {
            continue;
        }
        if (replacements[part] !== undefined) {
            children.push(replacements[part]);
        } else {
            children.push(elOf('span', null, part));
        }
    }

    return elOf(wrapperTag, wrapperClass, children);
}
